import {
  Cloud,
  Link2,
  Archive,
  Briefcase,
  Building,
  Building2,
  Eye,
  Lock,
  MessageSquareText,
  PencilLine,
  Pin,
  User,
  type LucideIcon,
} from "lucide-react";
import { clsx } from "clsx";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import type { ProjectResponseSchema } from "@entities/project";
import {
  useKindMeta,
  useProjectRoleLabel,
  useTemplateMeta,
} from "@entities/project";
import type { ProjectRole } from "@entities/session";
import { localeTag } from "@shared/lib";

type Project = z.infer<typeof ProjectResponseSchema>;

export type ProjectCardProps = {
  project: Project;
  onOpen: () => void;
  onTogglePin: () => void;
  onToggleArchive: () => void;
  /** Сервер, на котором у человека есть этот же проект: карточка получает пометку «Облако». */
  cloudHost?: string | undefined;
};

const ROLE_ICONS: Record<ProjectRole, LucideIcon> = {
  reader: Eye,
  reviewer: MessageSquareText,
  editor: PencilLine,
  owner: User,
};

const formatRelative = (
  isoString: string,
  locale: string,
  today: (time: string) => string,
): string => {
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return today(
      d.toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      }),
    );
  }
  return d.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

export const ProjectCard = ({
  project,
  onOpen,
  onTogglePin,
  onToggleArchive,
  cloudHost,
}: ProjectCardProps) => {
  const { t } = useTranslation("welcome");
  const { t: tCloud } = useTranslation("localStudio");
  const { t: tAccess } = useTranslation("projectAccess");
  const { t: tGuest } = useTranslation("guest");
  const roleLabel = useProjectRoleLabel();
  const meta = useTemplateMeta(project);
  const Icon = meta.icon;

  const hue = String(meta.accentHue);
  const accentBg = `oklch(60% 0.16 ${hue} / 0.14)`;
  const accentFg = `oklch(70% 0.16 ${hue})`;
  const accentLine = `oklch(60% 0.16 ${hue} / 0.3)`;

  // Тип проекта (исследовательский / прикладной) — рядом с иконкой шаблона,
  // чтобы вид работы был понятен с одного взгляда.
  const kindMeta = useKindMeta(project.kind);
  const KindIcon = kindMeta.icon;
  const kHue = String(kindMeta.hue);
  const kChroma = String(kindMeta.chroma);
  const kindBg = `oklch(60% ${kChroma} ${kHue} / 0.16)`;
  const kindFg = `oklch(70% ${kChroma} ${kHue})`;
  const kindLine = `oklch(60% ${kChroma} ${kHue} / 0.34)`;

  const isNda = project.meta?.nda === true;
  const supervisor = project.supervisor ?? null;

  // Серверные поля: папки у проекта нет, зато есть владелец и роль человека.
  // Шаблон в списке сервера не приходит, поэтому его значок прячем.
  const owner = project.owner ?? null;
  const role = project.my_role ?? null;
  const hasTemplate = owner === null;
  const canManage = role === null || role === "owner";
  const RoleIcon = role ? ROLE_ICONS[role] : null;
  const ownerLine = owner
    ? owner.kind === "org"
      ? {
          Icon: Building2,
          text: tAccess("card.ownerOrg", { name: owner.name }),
        }
      : role === "owner"
        ? { Icon: User, text: tAccess("card.ownerSelf") }
        : {
            Icon: User,
            text: tAccess("card.ownerUser", { name: owner.name }),
          }
    : null;

  return (
    <div
      className={clsx("card hover relative", project.archived && "opacity-60")}
      style={{ padding: "1.125rem" }}
      // Карточка остаётся <div role="button">: внутри уже есть настоящие
      // кнопки (закрепить / архив), а <button> внутри <button> невалиден.
      role="button"
      tabIndex={0}
      aria-label={project.name}
      data-project-card={project.id}
      onClick={onOpen}
      onKeyDown={(e) => {
        // Enter/Space с вложенных кнопок всплывают сюда — иначе «закрепить»
        // с клавиатуры заодно открывало бы проект.
        if (e.target !== e.currentTarget) return;
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        onOpen();
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: "0.75rem" }}
      >
        {/* minWidth: 0 инлайном (утилита min-w-0 в этой сборке Tailwind не
            генерируется) + truncate на тексте: длинное название шаблона
            обрезается «…», не выталкивая кнопки. */}
        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
          <div
            className="flex items-center justify-center shrink-0"
            style={{
              width: "2rem",
              height: "2rem",
              borderRadius: "0.5rem",
              background: accentBg,
              color: accentFg,
            }}
          >
            <Icon size="1rem" />
          </div>
          {hasTemplate && (
            <span
              className="chip"
              title={meta.label}
              style={{
                minWidth: 0,
                background: accentBg,
                color: accentFg,
                borderColor: accentLine,
              }}
            >
              <span className="truncate">{meta.label}</span>
            </span>
          )}
          <span
            className="chip shrink-0"
            title={kindMeta.label}
            style={{
              gap: "0.25rem",
              background: kindBg,
              color: kindFg,
              borderColor: kindLine,
            }}
          >
            <KindIcon size={11} />
            {kindMeta.label}
          </span>
        </div>
        <div
          className="flex items-center gap-1 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {/* Закрепление и архив меняют сам проект (PATCH, только владелец), поэтому
              остальным ролям кнопок нет. В локальном режиме роли нет, кнопки есть. */}
          {canManage && (
            <>
              <button
                type="button"
                onClick={onTogglePin}
                data-testid="project-pin"
                aria-label={project.pinned ? t("card.unpin") : t("card.pin")}
                aria-pressed={project.pinned}
                className={clsx("icon-btn sm tt", project.pinned && "active")}
                data-tt={project.pinned ? t("card.unpin") : t("card.pin")}
                style={project.pinned ? { color: "var(--accent)" } : undefined}
              >
                <Pin size="0.6875rem" />
              </button>
              <button
                type="button"
                onClick={onToggleArchive}
                data-testid="project-archive"
                aria-label={
                  project.archived ? t("card.unarchive") : t("card.archive")
                }
                className="icon-btn sm tt"
                data-tt={
                  project.archived ? t("card.unarchive") : t("card.archive")
                }
              >
                <Archive size="0.6875rem" />
              </button>
            </>
          )}
        </div>
      </div>

      <h3
        className="text-fg-0"
        style={{
          margin: "0 0 0.375rem",
          fontSize: "0.9375rem",
          fontWeight: 600,
          lineHeight: 1.3,
        }}
      >
        {project.name}
      </h3>
      {ownerLine ? (
        <div
          className="flex items-center gap-2 dim"
          style={{
            fontSize: "0.71875rem",
            marginBottom: "0.875rem",
            minWidth: 0,
          }}
          data-testid="project-owner"
        >
          <ownerLine.Icon
            size={11}
            style={{ color: "var(--fg-3)", flexShrink: 0 }}
          />
          <span className="truncate" style={{ minWidth: 0 }}>
            {ownerLine.text}
          </span>
        </div>
      ) : (
        <div
          className="mono dim"
          style={{ fontSize: "0.6875rem", marginBottom: "0.875rem" }}
        >
          {project.folder}
        </div>
      )}

      <div
        className="flex items-center justify-between"
        style={{ paddingTop: "0.75rem", borderTop: "1px solid var(--border)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {supervisor && (
            <>
              <User
                size="0.6875rem"
                style={{ color: "var(--fg-3)", flexShrink: 0 }}
              />
              <span
                className="dim truncate"
                style={{ fontSize: "0.71875rem", minWidth: 0 }}
              >
                {supervisor.name}
              </span>
            </>
          )}
          {role !== null && role !== "owner" && RoleIcon && (
            <span
              className="chip shrink-0"
              data-testid="project-role"
              title={tAccess("card.myRole", { role: roleLabel(role) })}
              style={{
                fontSize: "0.59375rem",
                height: "1rem",
                padding: "1px 0.375rem",
              }}
            >
              <RoleIcon size={8} />
              {roleLabel(role)}
            </span>
          )}
          {cloudHost !== undefined && (
            <span
              className="chip shrink-0"
              data-testid="cloud-badge"
              title={tCloud("cloud.badgeTitle", { host: cloudHost })}
              style={{
                fontSize: "0.59375rem",
                height: "1rem",
                padding: "1px 0.375rem",
              }}
            >
              <Cloud size="0.5rem" />
              {tCloud("cloud.badge")}
            </span>
          )}
          {project.via_link === true && (
            <span
              className="chip shrink-0"
              data-testid={`project-card-via-link-${project.id}`}
              style={{
                fontSize: "0.59375rem",
                height: "1rem",
                padding: "1px 0.375rem",
              }}
            >
              <Link2 size="0.5rem" />
              {tGuest("card.viaLink")}
            </span>
          )}
          {supervisor?.role === "company" && (
            <span
              className="chip shrink-0"
              style={{
                fontSize: "0.59375rem",
                height: "1rem",
                padding: "1px 0.375rem",
              }}
            >
              <Briefcase size="0.5rem" />
              {t("card.company")}
            </span>
          )}
          {supervisor?.role === "university" && (
            <span
              className="chip shrink-0"
              style={{
                fontSize: "0.59375rem",
                height: "1rem",
                padding: "1px 0.375rem",
              }}
            >
              <Building size="0.5rem" />
              {t("card.university")}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isNda && (
            <span
              className="chip"
              style={{
                color: "var(--c-warn)",
                borderColor:
                  "color-mix(in oklch, var(--c-warn) 30%, transparent)",
                background: "var(--c-warn-soft)",
              }}
            >
              <Lock size="0.5rem" />
              NDA
            </span>
          )}
          <span className="mono dim" style={{ fontSize: "0.65625rem" }}>
            {(project.lang ?? "ru").toUpperCase()}
          </span>
          <span className="mono dim" style={{ fontSize: "0.65625rem" }}>
            ·{" "}
            {formatRelative(project.updated_at, localeTag(), (time) =>
              t("card.today", { time }),
            )}
          </span>
        </div>
      </div>
    </div>
  );
};
